import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationFeature } from '@features/settings/enums/organizationFeature'
import { useOrganizationFeature } from '@features/settings/hooks/useOrganizationFeature'

import { FecImportSessionContextProvider, useFecImportSessionContext } from './contexts/fecImportSessionContext'
import { FecImportModal } from './elements/FecImportModal'
import { FecImportWizardContent } from './elements/FecImportWizardContent'
import { useCloseWhenFecImportFeatureDisabled } from './hooks/useCloseWhenFecImportFeatureDisabled'
import { useTrackFecImport } from './hooks/useTrackFecImport'
import { getFecImportModalChrome } from './utils/getFecImportModalChrome'
import { getFecImportTrackingIds } from './utils/getFecImportTrackingIds'
import { getFecImportTrackingStep } from './utils/getFecImportTrackingStep'

// The Resolve items screen (ADVP-600) doesn't exist yet; when it does, this is true only if there is something to resolve.
const HAS_RESOLVE_ITEMS_STEP = false

interface FecImportProps {
  onClose: () => void
}

const FecImportContent = ({ onClose }: FecImportProps): ReactElement | null => {
  const [isFinalisingMappings, setIsFinalisingMappings] = useState(false)
  const { t } = useTranslation()
  const { trackMappingsConfirmed, trackStepViewed, trackSuccessClosed, trackWizardClosed } = useTrackFecImport()
  const { hasFeature, isLoading: isOrganizationFeatureLoading } = useOrganizationFeature()
  const session = useFecImportSessionContext()

  const { resumableFecImports, sessionFecImports } = session
  const fecImportIds = useMemo(
    () => getFecImportTrackingIds({ resumableFecImports, sessionFecImports }),
    [resumableFecImports, sessionFecImports],
  )
  const trackingStep = getFecImportTrackingStep({
    isFinalisingMappings,
    isMappingsConfirmed: session.isMappingsConfirmed,
    isOrganizationFeatureLoading,
    step: session.step,
  })

  useEffect(() => {
    if (trackingStep) {
      trackStepViewed({ fecImportIds, step: trackingStep })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one event per screen, not per change of the import list
  }, [trackingStep, trackStepViewed])

  const steps = useMemo(
    () => [
      { label: t('settings.organization.accounting.fec_file_import.modal.stepper.step1') },
      ...(HAS_RESOLVE_ITEMS_STEP
        ? [{ label: t('settings.organization.accounting.fec_file_import.modal.stepper.resolve_items') }]
        : []),
      { label: t('settings.organization.accounting.fec_file_import.modal.stepper.step2') },
      { label: t('settings.organization.accounting.fec_file_import.modal.stepper.step3') },
    ],
    [t],
  )

  const handleModalClose = useCallback(() => {
    if (trackingStep) {
      trackWizardClosed({ fecImportIds, step: trackingStep })
    }

    if (session.isMappingsConfirmed) {
      trackSuccessClosed()
      session.onResetSession()
    }

    onClose()
  }, [fecImportIds, onClose, session, trackSuccessClosed, trackingStep, trackWizardClosed])

  const handleFinaliseSetup = useCallback(() => {
    setIsFinalisingMappings(true)
  }, [])

  const handleReviewMappings = useCallback(() => {
    setIsFinalisingMappings(false)
  }, [])

  const handleConfirmSuccess = useCallback(() => {
    session.setMappingsConfirmed()
    setIsFinalisingMappings(false)
    trackMappingsConfirmed({ importCount: session.sessionFecImports.length })
  }, [session, trackMappingsConfirmed])

  useCloseWhenFecImportFeatureDisabled({
    hasFeature,
    isLoading: isOrganizationFeatureLoading,
    onClose: handleModalClose,
  })

  if (!isOrganizationFeatureLoading && !hasFeature(OrganizationFeature.FecFileImport)) {
    return null
  }

  const modalChrome = getFecImportModalChrome({
    defaultTitle: t('settings.organization.accounting.fec_file_import.modal.title'),
    finaliseTitle: t('settings.organization.accounting.fec_file_import.modal.finalise_step.page_title'),
    hasResolveItemsStep: HAS_RESOLVE_ITEMS_STEP,
    isFinalisingMappings,
    isMappingsConfirmed: session.isMappingsConfirmed,
    isOrganizationFeatureLoading,
    mappingTitle: t('settings.organization.accounting.fec_file_import.modal.mapping_step.page_title'),
    step: session.step,
  })

  return (
    <FecImportModal
      currentStep={modalChrome.currentStep}
      isContentCentered={modalChrome.isContentCentered}
      onClose={handleModalClose}
      showStepper={modalChrome.showStepper}
      steps={steps}
      title={modalChrome.title}
    >
      <FecImportWizardContent
        isFinalisingMappings={isFinalisingMappings}
        isOrganizationFeatureLoading={isOrganizationFeatureLoading}
        onConfirmSuccess={handleConfirmSuccess}
        onFinaliseSetup={handleFinaliseSetup}
        onModalClose={handleModalClose}
        onReviewMappings={handleReviewMappings}
      />
    </FecImportModal>
  )
}

export const FecImport = ({ onClose }: FecImportProps): ReactElement => (
  <FecImportSessionContextProvider>
    <FecImportContent onClose={onClose} />
  </FecImportSessionContextProvider>
)
